import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3, Activity, TrendingUp, PieChart, Shield, Layers,
  LineChart, Bell, Database, Cpu, Globe, Zap, ArrowRight,
  Clock, Target, GitCompare, Calendar, Gauge, FileText, Menu, X
} from 'lucide-react';

const features = [
  {
    category: 'Real-Time Monitoring',
    accent: 'emerald',
    items: [
      { icon: Activity, title: 'Live Floating P&L', desc: 'See unrealized profit/loss across all accounts updated every 60 seconds. Balance vs equity gap visualization shows your true risk exposure.' },
      { icon: Zap, title: '60-Second Sync', desc: 'Your EA uploads data automatically every minute. No manual refresh, no stale data. See trades the moment they happen.' },
      { icon: Globe, title: 'Multi-Broker View', desc: 'Track unlimited accounts across Coinexx, Vantage, IC Markets, OctaFX, and 40+ other brokers — all in one dashboard.' },
    ]
  },
  {
    category: 'EA Performance Analytics',
    accent: 'cyan',
    items: [
      { icon: Cpu, title: 'Per-EA Breakdown', desc: 'Every magic number gets its own performance card: net profit, win rate, profit factor, expectancy, max drawdown, average trade duration.' },
      { icon: Target, title: 'EA Rating System', desc: 'Automatic EXCELLENT / GOOD / FAIR / POOR ratings based on profit factor, win rate, and drawdown metrics.' },
      { icon: GitCompare, title: 'Broker Comparison', desc: 'Same EA running on multiple brokers? Compare execution quality, slippage, swap costs, and net performance side by side.' },
    ]
  },
  {
    category: 'Advanced Analytics',
    accent: 'purple',
    items: [
      { icon: LineChart, title: 'Monte Carlo Simulation', desc: '200 randomized equity curves from your actual trades. See the probability distribution of outcomes and worst-case scenarios.' },
      { icon: PieChart, title: 'Session & Time Analysis', desc: 'Performance heatmaps by trading session (Asian/London/NY), hour of day, and day of week. Discover when EAs perform best.' },
      { icon: Calendar, title: 'Monthly Heatmap', desc: 'Bird\'s-eye view of monthly P&L across years. Spot seasonal patterns and identify your strongest/weakest months.' },
    ]
  },
  {
    category: 'Risk Management',
    accent: 'orange',
    items: [
      { icon: Shield, title: 'Floating DD Records', desc: 'Track daily, weekly, monthly, and all-time floating drawdown records. Know your worst moments and trends.' },
      { icon: Gauge, title: 'Drawdown Spike Analysis', desc: 'Top 5 worst drawdown moments with per-EA breakdown. See exactly which EA caused each spike.' },
      { icon: Bell, title: 'Threshold Alerts', desc: 'Set drawdown percentage alerts. Get notified before a bad day becomes a blown account.' },
    ]
  },
  {
    category: 'Reporting & Export',
    accent: 'pink',
    items: [
      { icon: FileText, title: 'PDF Reports', desc: 'Professional performance reports with summary stats, EA comparison tables, and recent trade history. Share with investors.' },
      { icon: Database, title: 'CSV Export', desc: 'Export full trade history, EA statistics, and account summaries as CSV. Your data is always portable.' },
      { icon: Clock, title: 'Period Analysis', desc: 'Filter everything by today, this week, this month, this year, or custom date ranges. Compare any two periods side by side.' },
    ]
  },
];

const ACCENTS = {
  emerald: { color: '#10b981', bg: 'rgba(16,185,129,0.1)', border: 'rgba(16,185,129,0.25)', glow: 'rgba(16,185,129,0.15)', gradient: 'linear-gradient(180deg, #10b981, #14b8a6)' },
  cyan: { color: '#22d3ee', bg: 'rgba(34,211,238,0.1)', border: 'rgba(34,211,238,0.25)', glow: 'rgba(34,211,238,0.15)', gradient: 'linear-gradient(180deg, #22d3ee, #0891b2)' },
  purple: { color: '#a855f7', bg: 'rgba(168,85,247,0.1)', border: 'rgba(168,85,247,0.25)', glow: 'rgba(168,85,247,0.15)', gradient: 'linear-gradient(180deg, #a855f7, #8b5cf6)' },
  orange: { color: '#f97316', bg: 'rgba(249,115,22,0.1)', border: 'rgba(249,115,22,0.25)', glow: 'rgba(249,115,22,0.15)', gradient: 'linear-gradient(180deg, #f97316, #ea580c)' },
  pink: { color: '#ec4899', bg: 'rgba(236,72,153,0.1)', border: 'rgba(236,72,153,0.25)', glow: 'rgba(236,72,153,0.15)', gradient: 'linear-gradient(180deg, #ec4899, #db2777)' },
};

export default function FeaturesPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#070912] text-gray-100 overflow-x-hidden relative">
      {/* Ambient mesh gradient background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-purple-600/8 blur-[140px] animate-float-slow" />
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full bg-cyan-500/6 blur-[130px] animate-float" />
        <div className="absolute bottom-0 left-1/3 w-[550px] h-[550px] rounded-full bg-emerald-500/6 blur-[140px] animate-float-slow" />
      </div>

      {/* Nav */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-[#070912]/85 backdrop-blur-xl border-b border-white/8' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 bg-gradient-to-br from-emerald-400 via-teal-400 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:scale-105 transition-transform">
              <BarChart3 size={18} className="text-white" />
            </div>
            <span className="text-lg font-bold text-white font-display">TrackMyFxEA</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/explore" className="text-sm text-gray-400 hover:text-white transition-colors">Explore</Link>
            <Link to="/features" className="text-sm text-emerald-400 font-medium">Features</Link>
            <Link to="/how-it-works" className="text-sm text-gray-400 hover:text-white transition-colors">How It Works</Link>
            <Link to="/pricing" className="text-sm text-gray-400 hover:text-white transition-colors">Pricing</Link>
            <Link to="/ea-store" className="text-sm text-gray-400 hover:text-white transition-colors">EA Store</Link>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <Link to="/login" className="text-sm text-gray-300 hover:text-white transition-colors px-4 py-2">Sign In</Link>
            <Link to="/register" className="text-sm bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-emerald-500/30 transition-all">Get Started</Link>
          </div>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-gray-300 hover:text-white p-2">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/8 bg-[#070912]/95 backdrop-blur-xl px-4 py-4 space-y-3">
            <Link to="/explore" className="block text-sm text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>Explore</Link>
            <Link to="/features" className="block text-sm text-emerald-400 py-2" onClick={() => setMobileMenuOpen(false)}>Features</Link>
            <Link to="/how-it-works" className="block text-sm text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>How It Works</Link>
            <Link to="/pricing" className="block text-sm text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
            <Link to="/ea-store" className="block text-sm text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>EA Store</Link>
            <hr className="border-white/8" />
            <Link to="/login" className="block text-sm text-gray-300 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>Sign In</Link>
            <Link to="/register" className="block text-sm bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium px-5 py-2.5 rounded-xl text-center" onClick={() => setMobileMenuOpen(false)}>Get Started</Link>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 px-4 md:px-6 text-center relative">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 mb-7 backdrop-blur-sm animate-fade-in-down">
            <Zap size={12} className="text-emerald-400" />
            <span className="text-xs text-gray-300">Built for serious traders</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-5 font-display leading-tight animate-fade-in-up">
            Every Feature a<br />
            <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">Serious Trader Needs</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-base md:text-lg leading-relaxed animate-fade-in-up">
            From real-time sync to Monte Carlo simulations — built for traders who run EAs professionally.
          </p>
        </div>
      </section>

      {/* Feature Sections */}
      <section className="px-4 md:px-6 pb-20 relative">
        <div className="max-w-6xl mx-auto space-y-16 md:space-y-24">
          {features.map(({ category, items, accent }, idx) => {
            const a = ACCENTS[accent];
            return (
              <div key={category} className="relative">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-1 h-9 rounded-full" style={{ background: a.gradient }} />
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: a.color }}>0{idx + 1}</span>
                  <h2 className="text-2xl md:text-3xl font-bold text-white font-display">{category}</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                  {items.map(({ icon: Icon, title, desc }) => (
                    <div key={title} className="group relative p-6 rounded-2xl border transition-all duration-300 hover:-translate-y-1 backdrop-blur-sm overflow-hidden" style={{
                      background: 'rgba(255,255,255,0.02)',
                      borderColor: 'rgba(255,255,255,0.08)',
                    }} onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = a.border;
                      e.currentTarget.style.background = `linear-gradient(135deg, ${a.bg.replace('0.1', '0.04')}, transparent)`;
                      e.currentTarget.style.boxShadow = `0 8px 32px rgba(0,0,0,0.3), 0 0 30px ${a.glow}`;
                    }} onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                      e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}>
                      <div className="absolute -top-12 -right-12 w-24 h-24 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-2xl pointer-events-none" style={{ background: a.glow }} />
                      <div className="relative">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110" style={{ background: a.bg, boxShadow: `0 0 20px ${a.glow}` }}>
                          <Icon size={22} style={{ color: a.color }} />
                        </div>
                        <h3 className="text-white font-semibold mb-2 text-lg">{title}</h3>
                        <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 md:px-6 py-20 md:py-24 bg-[#080c14] text-center relative">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-5 font-display">
            Ready to See Your EA Performance{' '}
            <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">Clearly?</span>
          </h2>
          <p className="text-gray-400 mb-9 text-base md:text-lg">Free plan includes all core features. No credit card required.</p>
          <Link to="/register" className="inline-flex items-center gap-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold px-8 py-4 rounded-xl text-base md:text-lg hover:shadow-xl hover:shadow-emerald-500/40 hover:-translate-y-0.5 transition-all">
            Start Free <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/8 px-6 py-8 text-center text-xs text-gray-600 bg-[#060810]">
        <p>© 2026 TrackMyFxEA. All rights reserved.</p>
        <p className="mt-2"><a href="mailto:support@trackmyfxea.com" className="hover:text-gray-400 transition-colors">support@trackmyfxea.com</a></p>
<p className="mt-3"><Link to="/privacy" className="mx-2 hover:text-gray-400 transition-colors">Privacy</Link><Link to="/terms" className="mx-2 hover:text-gray-400 transition-colors">Terms</Link><Link to="/risk-disclaimer" className="mx-2 hover:text-gray-400 transition-colors">Risk Disclaimer</Link></p>
      </footer>
    </div>
  );
}
